import { useState } from "react";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import PlayingCard from "./PlayingCard";
import { X, ArrowLeftRight, Coins, ArrowDown, ArrowUp, Lock } from "lucide-react";
import { toast } from "sonner";

const MAX_CHIPS_PER_TRADE = 6;

export default function TradePanel({ 
  targetPlayer, 
  myCards, 
  myChips,
  onSubmit, 
  onClose 
}) {
  const [offerCardIndices, setOfferCardIndices] = useState([]);
  const [requestCardIndices, setRequestCardIndices] = useState([]);
  const [offerChips, setOfferChips] = useState(0);
  const [requestChips, setRequestChips] = useState(0);

  // Get my PUBLIC cards only (non-public cards cannot be traded)
  const myPublicCards = myCards?.map((card, index) => ({ ...card, originalIndex: index }))
    .filter(c => c.is_public) || [];
  
  // Get target player's public cards
  const targetPublicCards = targetPlayer.cards?.filter(c => c.is_public && c.suit !== "hidden") || [];

  // No validation on card selection - just toggle
  const handleMyCardToggle = (originalIndex) => {
    setOfferCardIndices(prev => {
      if (prev.includes(originalIndex)) {
        return prev.filter(i => i !== originalIndex);
      }
      return [...prev, originalIndex];
    });
  };

  const handleTargetCardToggle = (index) => {
    setRequestCardIndices(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      return [...prev, index];
    });
  };

  const handleSubmit = () => {
    // Validate: must have something to trade
    if (offerCardIndices.length === 0 && offerChips === 0 && 
        requestCardIndices.length === 0 && requestChips === 0) {
      toast.error("取引内容を選択してください");
      return;
    }

    // Validate: must keep at least 2 public cards after trade
    const publicCardsAfterTrade = myPublicCards.length - offerCardIndices.length + requestCardIndices.length;
    if (publicCardsAfterTrade < 2) {
      toast.error("取引後に公開カードが2枚以上必要です");
      return;
    }

    // Validate: must keep at least 5 cards total
    const totalCardsAfterTrade = myCards.length - offerCardIndices.length + requestCardIndices.length;
    if (totalCardsAfterTrade < 5) {
      toast.error("取引後に5枚以上のカードが必要です");
      return;
    }

    // Convert request card indices to original indices
    const targetFullCards = targetPlayer.cards || [];
    const publicCardOriginalIndices = [];
    let publicIdx = 0;
    for (let i = 0; i < targetFullCards.length; i++) {
      if (targetFullCards[i].is_public && targetFullCards[i].suit !== "hidden") {
        if (requestCardIndices.includes(publicIdx)) {
          publicCardOriginalIndices.push(i);
        }
        publicIdx++;
      }
    }

    onSubmit({
      offer_cards: offerCardIndices,
      offer_chips: offerChips,
      request_cards: publicCardOriginalIndices,
      request_chips: requestChips
    });
  };

  const maxOfferChips = Math.min(myChips, MAX_CHIPS_PER_TRADE);
  const maxRequestChips = Math.min(targetPlayer.chips, MAX_CHIPS_PER_TRADE);

  // Get non-public cards count
  const myPrivateCardsCount = myCards?.filter(c => !c.is_public).length || 0;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-2" data-testid="trade-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      
      {/* Panel - Centered modal instead of bottom sheet */}
      <div className="relative w-full max-w-2xl bg-card border border-brass/30 rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-brass" />
            <span className="font-display text-base">{targetPlayer.nickname}との取引</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content - Two columns layout */}
        <div className="p-3 grid grid-cols-2 gap-3">
          {/* Left: My Offer */}
          <div className="p-3 bg-red-900/10 rounded-lg border border-red-900/30">
            <div className="flex items-center gap-1 mb-2">
              <ArrowUp className="w-3 h-3 text-red-400" />
              <h3 className="text-xs font-medium">渡す</h3>
            </div>
            
            {/* My Cards */}
            <div className="mb-2">
              <div className="flex flex-wrap gap-1 min-h-[50px]">
                {myPublicCards.map((card) => (
                  <div
                    key={card.originalIndex}
                    onClick={() => handleMyCardToggle(card.originalIndex)}
                    className={`cursor-pointer transition-all ${
                      offerCardIndices.includes(card.originalIndex) 
                        ? "ring-2 ring-red-400 -translate-y-1 scale-105" 
                        : "hover:opacity-80"
                    }`}
                  >
                    <PlayingCard suit={card.suit} rank={card.rank} isPublic={true} size="xs" />
                  </div>
                ))}
              </div>
              {myPrivateCardsCount > 0 && (
                <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
                  <Lock className="w-2.5 h-2.5" />
                  非公開{myPrivateCardsCount}枚
                </div>
              )}
            </div>

            {/* Offer Chips */}
            <div className="flex items-center gap-2">
              <Coins className="w-3 h-3 text-brass flex-shrink-0" />
              <Slider
                value={[offerChips]}
                onValueChange={([val]) => setOfferChips(val)}
                max={maxOfferChips}
                step={1}
                className="flex-1"
              />
              <span className="text-xs w-8 text-right">{offerChips}</span>
            </div>
          </div>

          {/* Right: Request */}
          <div className="p-3 bg-green-900/10 rounded-lg border border-green-900/30">
            <div className="flex items-center gap-1 mb-2">
              <ArrowDown className="w-3 h-3 text-green-400" />
              <h3 className="text-xs font-medium">受取</h3>
            </div>

            {/* Target Cards */}
            <div className="mb-2">
              <div className="flex flex-wrap gap-1 min-h-[50px]">
                {targetPublicCards.length > 0 ? (
                  targetPublicCards.map((card, index) => (
                    <div
                      key={index}
                      onClick={() => handleTargetCardToggle(index)}
                      className={`cursor-pointer transition-all ${
                        requestCardIndices.includes(index) 
                          ? "ring-2 ring-green-400 -translate-y-1 scale-105" 
                          : "hover:opacity-80"
                      }`}
                    >
                      <PlayingCard suit={card.suit} rank={card.rank} isPublic={true} size="xs" />
                    </div>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">公開カードなし</span>
                )}
              </div>
            </div>

            {/* Request Chips */}
            <div className="flex items-center gap-2">
              <Coins className="w-3 h-3 text-brass flex-shrink-0" />
              <Slider
                value={[requestChips]}
                onValueChange={([val]) => setRequestChips(val)}
                max={maxRequestChips}
                step={1}
                className="flex-1"
              />
              <span className="text-xs w-8 text-right">{requestChips}</span>
            </div>
          </div>
        </div>

        {/* Summary & Actions - Compact footer */}
        <div className="p-3 border-t border-border flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground flex-1">
            <span className="text-red-400">渡:{offerCardIndices.length}枚{offerChips > 0 && `+${offerChips}C`}</span>
            <ArrowLeftRight className="w-3 h-3 inline mx-1" />
            <span className="text-green-400">受:{requestCardIndices.length}枚{requestChips > 0 && `+${requestChips}C`}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              キャンセル
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              className="bg-brass text-accent-foreground hover:bg-brass/90"
            >
              提案
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
